import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

// Antworttypen des Geocoders, die einen Ort bzw. Ortsteil bezeichnen
const CITY_TYPES = ["city", "locality"];
const DISTRICT_TYPES = ["district"];
const STREET_TYPES = ["street"];
const KNOWN_TYPES = ["house", "street", "city", "district", "locality", "county", "state", "country", "other"];

function mapsKey() {
  if (typeof document === "undefined" || !document.documentElement) {
    return "";
  }
  return document.documentElement.dataset.mapsKey || "";
}

function str(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

function validLatLon(lat, lon) {
  return typeof lat === "number" && typeof lon === "number"
    && isFinite(lat) && isFinite(lon)
    && lat >= -90 && lat <= 90
    && lon >= -180 && lon <= 180;
}

function cancelledError() {
  const err = new Error("geocoder request cancelled");
  err.cancelled = true;
  return err;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
  }

  cancelAutocomplete() {
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g, label) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    // Ohne Orts- und Strassenangabe den Feature-Namen als Beschriftung behalten
    if (!s && !g.street && label) {
      p.push(label);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _type(props) {
    const type = str(props.type);
    if (type && KNOWN_TYPES.indexOf(type) !== -1) {
      return type;
    }

    // Legacy-Fallback ueber OSM-Klassifikation
    if (props.osm_key == "highway") {
      return "street";
    }
    if (props.osm_key == "boundary" || (props.osm_key == "place" && props.osm_value == "suburb")) {
      return "district";
    }
    if (props.osm_key == "place") {
      return props.osm_value == "house" ? "house" : "city";
    }

    return "other";
  }

  // Wandelt ein Feature in eine Geoadresse um, ohne es zu veraendern.
  _geoaddress(l) {
    if (!l || typeof l !== "object" || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const lng = Number(l.geometry.coordinates[0]);
    const lat = Number(l.geometry.coordinates[1]);
    if (!validLatLon(lat, lng)) {
      return null;
    }

    const props = l.properties && typeof l.properties === "object" ? l.properties : {};
    const type = this._type(props);
    const name = str(props.name);

    const geoaddress = {};

    let street = str(props.street);
    if (!street && STREET_TYPES.indexOf(type) !== -1) {
      street = name;
    }
    if (street) {
      geoaddress["street"] = street;
      const number = str(props.housenumber);
      if (number) {
        geoaddress["number"] = number;
      }
    }

    const zip = str(props.postcode);
    if (zip) {
      geoaddress["zip"] = zip;
    }

    let city = str(props.city);
    if (!city && CITY_TYPES.indexOf(type) !== -1) {
      city = name;
    }
    if (city) {
      geoaddress["city"] = city;
    }

    let district = str(props.district);
    if (DISTRICT_TYPES.indexOf(type) !== -1 && name && name !== city) {
      district = name;
    }
    if (district && district !== city) {
      geoaddress["district"] = district;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = str(props.state);
    geoaddress["country"] = str(props.country);

    geoaddress["text"] = this._geoaddressText(geoaddress, name);

    return geoaddress;
  }

  _features(res) {
    return res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
  }

  geocode(params) {
    const request = this.axiosRequest;
    const key = mapsKey();

    if (!key) {
      this.axiosRequest = null;
      return Promise.resolve([]);
    }

    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      const results = [];
      this._features(res).forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });
      return results;
    }, thrown => {
      if (this.axios.isCancel(thrown)) {
        throw cancelledError();
      }
      throw thrown;
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const CancelToken = this.axios.CancelToken;
    this.axiosRequest = {
      source: CancelToken.source()
    };

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("layer", "city");
    params.append("layer", "district");
    params.append("layer", "locality");

    return this.geocode(params);
  }

  // Liefert die Geoadresse oder null; endet immer (Timeout, kein Key, Fehler).
  reverse(lat, lng) {
    if (lat === null || lat === undefined || lat === "" || lng === null || lng === undefined || lng === "") {
      return Promise.resolve(null);
    }
    lat = Number(lat);
    lng = Number(lng);

    const key = mapsKey();
    if (!key || !validLatLon(lat, lng)) {
      return Promise.resolve(null);
    }

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT,
      params: {
        lat: lat,
        lon: lng,
        lang: "de",
        key: key
      }
    }).then(res => {
      const features = this._features(res);
      return features.length > 0 ? this._geoaddress(features[0]) : null;
    }).catch(err => {
      console.warn("Geocoder reverse failed", err && err.message);
      return null;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
